import React, { useEffect, useMemo, useRef, useState } from 'react'

import { useMutation } from '@apollo/client'
import { E164Number } from 'libphonenumber-js/core'
import { isEqual } from 'lodash-es'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { isPossiblePhoneNumber } from 'react-phone-number-input'

import { UPDATE_CUSTOMER } from 'Const'
import { Customer } from 'Types'
import { noty } from 'Utils'
import { normalizePhone } from 'Utils/customer'

import { DateField } from 'Components/DateField'
import PhoneInput from 'Components/PhoneInput'

import { Checkbox } from '../../components/Checkbox'
import { Field } from '../../components/Field'
import { PhoneList } from '../../components/PhoneList'
import { SaveBar } from '../../components/SaveBar'

interface Props {
  customer: Customer
}

interface AdditionalPhone {
  phone: E164Number | undefined
}

interface FormValues {
  additionalPhones: AdditionalPhone[]
  birthday: string
  firstName: string
  lastName: string
  phone: E164Number | undefined
  subscribed: boolean
  subscribedSms: boolean
}

const toDateInputValue = (birthday: string | null): string => (birthday ? birthday.slice(0, 10) : '')

const isCompletePhone = (value: E164Number | undefined): boolean =>
  Boolean(value) && isPossiblePhoneNumber(String(value))

const PHONE_INVALID_MESSAGE = 'This phone number doesn’t look valid. Check the country and digits, then try again.'

const phonesEqual = (a: AdditionalPhone[] | undefined, b: AdditionalPhone[] | undefined): boolean => {
  const normalizedA = (a ?? []).map((item) => normalizePhone(item.phone)).filter(Boolean)
  const normalizedB = (b ?? []).map((item) => normalizePhone(item.phone)).filter(Boolean)
  return normalizedA.length === normalizedB.length && normalizedA.every((phone, index) => phone === normalizedB[index])
}

const toFormValues = (customer: Customer): FormValues => ({
  additionalPhones: ((customer.additionalPhones as unknown as E164Number[] | undefined) ?? []).map((phone) => ({
    phone,
  })),
  birthday: toDateInputValue(customer.birthday),
  firstName: customer.firstName,
  lastName: customer.lastName,
  phone: customer.phone as E164Number | undefined,
  subscribed: customer.subscribed,
  subscribedSms: customer.subscribedSms,
})

export const ProfileTab: React.FC<Props> = ({ customer }) => {
  const phoneRequired = !customer.phone
  const locationCountryIsoCodes = window.VARIABLES?.locationCountryIsoCodes ?? []
  const defaultCountry = locationCountryIsoCodes[0] || 'US'

  const defaultValues = useMemo(() => toFormValues(customer), [customer])
  const { clearErrors, control, formState, handleSubmit, register, reset, setError, setValue } = useForm<FormValues>({
    defaultValues,
    shouldUnregister: false,
  })
  const values = useWatch({ control, defaultValue: defaultValues }) as FormValues

  const appliedDefaultValuesRef = useRef(defaultValues)
  useEffect(() => {
    if (isEqual(appliedDefaultValuesRef.current, defaultValues)) return
    appliedDefaultValuesRef.current = defaultValues
    reset(defaultValues)
  }, [defaultValues, reset])

  const [updateCustomer, { loading }] = useMutation(UPDATE_CUSTOMER)

  const isDirty =
    formState.dirtyFields.firstName ||
    formState.dirtyFields.lastName ||
    formState.dirtyFields.birthday ||
    formState.dirtyFields.subscribed ||
    formState.dirtyFields.subscribedSms ||
    (phoneRequired && normalizePhone(values.phone) !== normalizePhone(defaultValues.phone)) ||
    !phonesEqual(values.additionalPhones, defaultValues.additionalPhones)

  const [status, setStatus] = useState('')
  const [leaving, setLeaving] = useState(false)
  const showBar = isDirty || status === 'Saved' || leaving

  const handleCancel = () => reset(defaultValues)

  const onSubmit = handleSubmit(async (data) => {
    if (phoneRequired && !isCompletePhone(data.phone)) {
      setError('phone', { type: 'validate', message: PHONE_INVALID_MESSAGE })
      noty(PHONE_INVALID_MESSAGE, 'error')
      document.getElementById('profile-phone')?.focus()
      return
    }

    const invalidIndex = (data.additionalPhones ?? []).findIndex((item) => !isCompletePhone(item.phone))
    if (invalidIndex !== -1) {
      setError(`additionalPhones.${invalidIndex}.phone`, { type: 'validate', message: PHONE_INVALID_MESSAGE })
      noty(PHONE_INVALID_MESSAGE, 'error')
      document.getElementById(`profile-additional-phone-${invalidIndex}`)?.focus()
      return
    }

    try {
      await updateCustomer({
        variables: {
          input: {
            firstName: data.firstName,
            lastName: data.lastName,
            birthday: data.birthday ? `${data.birthday}T00:00:00Z` : null,
            additionalPhones: (data.additionalPhones ?? []).map((item) => item.phone).filter(Boolean),
            subscribed: data.subscribed,
            subscribedSms: data.subscribedSms,
            ...(phoneRequired ? { phone: data.phone } : {}),
          },
        },
      })
      setLeaving(false)
      setStatus('Saved')
    } catch (err) {
      noty(err instanceof Error ? err.message : 'Unable to save profile', 'error')
    }
  })

  // Save-bar reveal/hide state machine — clear the "Saved" flash once the
  // form goes dirty again, adjusted during render rather than in an effect.
  const [prevDirty, setPrevDirty] = useState(isDirty)
  if (isDirty !== prevDirty) {
    setPrevDirty(isDirty)
    if (isDirty) {
      setLeaving(false)
      setStatus((current) => (current === 'Saved' ? '' : current))
    }
  }

  // Auto-dismiss the bar a few seconds after saving.
  useEffect(() => {
    if (status !== 'Saved' || isDirty) return undefined

    const hide = window.setTimeout(() => setLeaving(true), 3000)
    return () => window.clearTimeout(hide)
  }, [status, isDirty])

  useEffect(() => {
    if (!leaving) return undefined

    const done = window.setTimeout(() => {
      setStatus('')
      setLeaving(false)
    }, 280)
    return () => window.clearTimeout(done)
  }, [leaving])

  return (
    <form
      className="user-profile-panel user-profile-panel_enter"
      id="profile-form"
      onSubmit={(event) => void onSubmit(event)}
    >
      <section className="user-profile-section">
        <h2 className="user-profile-section__title">Personal information</h2>
        <Field htmlFor="profile-first-name" label="First name">
          <input ref={register()} id="profile-first-name" name="firstName" />
        </Field>
        <Field htmlFor="profile-last-name" label="Last name">
          <input ref={register()} id="profile-last-name" name="lastName" />
        </Field>
        <Field htmlFor="profile-email" label="Email address">
          <input disabled id="profile-email" value={customer.email} />
        </Field>
        <Field
          error={formState.errors.phone?.message}
          htmlFor="profile-phone"
          label={`Phone number${phoneRequired ? ' *' : ''}`}
        >
          {phoneRequired ? (
            <Controller
              control={control}
              name="phone"
              render={(field) => (
                <PhoneInput
                  {...field}
                  required
                  countryOptionsOrder={locationCountryIsoCodes}
                  defaultCountry={defaultCountry}
                  id="profile-phone"
                  numberInputProps={{
                    'aria-describedby': formState.errors.phone ? 'profile-phone-error' : undefined,
                    'aria-invalid': Boolean(formState.errors.phone),
                    id: 'profile-phone',
                  }}
                  onChange={(value) => {
                    field.onChange(value)
                    clearErrors('phone')
                  }}
                />
              )}
            />
          ) : (
            <input disabled id="profile-phone" value={customer.phone} />
          )}
        </Field>
        <Field htmlFor="profile-birthday" label="Birthday">
          <Controller
            control={control}
            name="birthday"
            render={(field) => (
              <DateField
                autoComplete="bday"
                id="profile-birthday"
                inputRef={field.ref}
                name={field.name}
                popoverLabel="Choose a birthday"
                value={field.value || ''}
                onBlur={field.onBlur}
                onChange={field.onChange}
              />
            )}
          />
        </Field>
      </section>

      <section className="user-profile-section">
        <h2 className="user-profile-section__title">Additional phones</h2>
        <PhoneList
          control={control}
          countryOptionsOrder={locationCountryIsoCodes}
          defaultCountry={defaultCountry}
          phoneErrors={formState.errors.additionalPhones}
          setValue={setValue}
          onPhoneChange={(name) => clearErrors(name)}
        />
      </section>

      <section className="user-profile-section">
        <h2 className="user-profile-section__title">Updates</h2>
        <Controller
          control={control}
          name="subscribed"
          render={(field) => (
            <Checkbox
              checked={Boolean(field.value)}
              hint="Occasional news and offers"
              id="profile-subscribed-email"
              label="Subscribe to email updates"
              onChange={field.onChange}
            />
          )}
        />
        <Controller
          control={control}
          name="subscribedSms"
          render={(field) => (
            <Checkbox
              checked={Boolean(field.value)}
              hint="Order and reservation reminders"
              id="profile-subscribed-sms"
              label="Subscribe to SMS updates"
              onChange={field.onChange}
            />
          )}
        />
      </section>

      {showBar ? (
        <SaveBar
          formId="profile-form"
          leaving={leaving}
          saved={status === 'Saved'}
          saving={loading}
          onCancel={handleCancel}
        />
      ) : null}
    </form>
  )
}
